import { POI } from '@cya-routes/shared'
import { Eye } from 'lucide-react'
import { POI_CATEGORIES } from '../../data/poiTypes'

interface POIFilterPanelProps {
  pois: POI[]
  hiddenPOICategories: Set<string>
  onToggleCategory: (categoryId: string) => void
  onShowAllCategories: () => void
  onHideAllCategories: () => void
}

export function POIFilterPanel({
  pois,
  hiddenPOICategories,
  onToggleCategory,
  onShowAllCategories,
  onHideAllCategories
}: POIFilterPanelProps) {
  const handleToggleAll = () => {
    if (hiddenPOICategories.size === POI_CATEGORIES.length) {
      onShowAllCategories()
    } else {
      onHideAllCategories()
    }
  }

  return (
    <div className="p-1 sm:p-2">
      <div className="hidden sm:flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white">POI Categories</h3>
        <button
          onClick={onShowAllCategories}
          className="text-xs text-emerald-400 hover:text-emerald-300"
        >
          Show All
        </button>
      </div>
      
      <div className="space-y-2">
        {/* Toggle All Button - styled like a category */}
        <button
          onClick={handleToggleAll}
          className={`w-[calc(100%-8px)] flex items-center gap-1 sm:gap-2 px-1 sm:px-2 py-1 sm:py-1.5 rounded-lg transition-all ${
            hiddenPOICategories.size === POI_CATEGORIES.length
              ? 'bg-gray-700/50 text-gray-400 hover:bg-gray-700' 
              : 'bg-gray-700 text-white hover:bg-gray-600'
          }`}
        >
          <div 
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ 
              backgroundColor: hiddenPOICategories.size === POI_CATEGORIES.length ? '#374151' : '#10b981',
              opacity: hiddenPOICategories.size === POI_CATEGORIES.length ? 0.5 : 1
            }}
          >
            <Eye className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-[10px] flex-1 text-left hidden sm:block">
            {hiddenPOICategories.size === POI_CATEGORIES.length ? 'Show All' : 'Hide All'}
          </span>
          <span className="text-[9px] text-gray-400">{pois.length}</span>
        </button>
        
        {/* Divider */}
        <div className="h-px bg-gray-600/50 my-1"></div>
        
        {POI_CATEGORIES.map(category => {
          const isHidden = hiddenPOICategories.has(category.id)
          const IconComponent = category.types[0]?.icon
          
          return (
            <button
              key={category.id}
              onClick={() => onToggleCategory(category.id)}
              className={`w-[calc(100%-8px)] flex items-center gap-1 sm:gap-2 px-1 sm:px-2 py-1 sm:py-1.5 rounded-lg transition-all ${
                isHidden 
                  ? 'bg-gray-700/50 text-gray-400 hover:bg-gray-700' 
                  : 'bg-gray-700 text-white hover:bg-gray-600'
              }`}
            >
              <div 
                className="w-5 h-5 rounded-md flex items-center justify-center"
                style={{ 
                  backgroundColor: isHidden ? '#374151' : category.color,
                  opacity: isHidden ? 0.5 : 1
                }}
              >
                {IconComponent && <IconComponent className="w-3.5 h-3.5 text-white" strokeWidth={1.5} />}
              </div>
              <span className="text-[10px] flex-1 text-left hidden sm:block">{category.name}</span>
              <span className="text-[9px] text-gray-400">
                {pois.filter(poi => 
                  category.types.some(type => type.id === poi.type)
                ).length}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}